/**
 * AlertAgentChat — agent chat panel for alert cards.
 * Self-contained: owns its own session, streaming, and message state.
 *
 * Two modes:
 * - Inline (default): compact panel inside expanded alert card
 * - Maximized: fills the content area, alert details are the first message
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Stethoscope, ArrowDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { useAgentChat } from '../../hooks/useAgentChat';
import { createAgentSessionFromAlert } from '../../services/alertService';
import { ChatMessage } from '../common/ChatMessage';
import { ICON_PX } from '../ui/lib/iconSizes';
import { useChatScroll } from '../../hooks/useChatScroll';

const SUGGESTED_QUESTIONS = [
  'What is the root cause?',
  'What actions should I take?',
  'How does this impact other metrics?',
];

export function AlertAgentChat({ alertId, alertSummary, initialMessages = [], maximized = false, onClose }) {
  const { getToken } = useAuth();
  const { isStreaming, streamingText, error, sendMessage } = useAgentChat({ getToken });

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  const { containerRef, bottomRef, showScrollBtn, scrollToBottom } = useChatScroll({
    isStreaming,
    deps: [messages.length, streamingText],
  });

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    setCreating(true);
    try {
      const res = await createAgentSessionFromAlert(alertId, getToken);
      const id = res.id || res.sessionId;
      setSessionId(id);
      return id;
    } catch (err) {
      console.error('Failed to create agent session:', err);
      return null;
    } finally {
      setCreating(false);
    }
  }, [alertId, getToken, sessionId]);

  const handleSend = useCallback(async (text) => {
    if (!text.trim() || isStreaming) return;

    const userMsg = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    const sid = await ensureSession();
    if (!sid) return;

    const result = await sendMessage(sid, text.trim());
    if (result?.text) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: result.text,
        messageId: result.messageId,
        timestamp: new Date().toISOString(),
      }]);
    }
  }, [isStreaming, ensureSession, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  // Only count user-sent messages (not initial context) for suggested chips
  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  return (
    <div className={`flex flex-col ${maximized ? 'h-full min-h-0' : 'mt-3 border-t border-nn-border pt-3'}`}>
      {/* Messages area */}
      <div ref={containerRef} className={`relative overflow-y-auto rounded border border-nn-border/50 bg-surface p-3 mb-2 ${maximized ? 'flex-1 min-h-0' : 'max-h-[400px]'}`}>
        {messages.length === 0 && !isStreaming && (
          <p className="text-xs text-text-muted text-center py-4">
            Ask questions about this alert — the agent has access to live operational data.
          </p>
        )}

        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}

        {isStreaming && streamingText && (
          <div className="flex justify-start mb-3">
            <div className="max-w-[85%] flex items-start gap-2">
              <div className="shrink-0 w-6 h-6 rounded-full bg-royal/20 flex items-center justify-center mt-0.5">
                <Stethoscope size={ICON_PX.sm} className="text-royal" />
              </div>
              <div className="bg-slate-50 border border-nn-border rounded-lg px-3 py-2 text-body-sm text-text-dark min-w-0">
                <div className="prose prose-sm max-w-none [&>p]:mb-1.5 [&>p:last-child]:mb-0 [&>ul]:mb-1.5 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:mb-1.5 [&>ol]:ml-4 [&>ol]:list-decimal [&>h3]:text-body [&>h3]:font-semibold [&>h3]:text-aqua [&>h3]:mt-3 [&>h3]:mb-1 [&>h3:first-child]:mt-0 [&_li]:mb-0.5">
                  <ReactMarkdown>{streamingText}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        )}

        {isStreaming && !streamingText && (
          <div className="flex items-center gap-2 text-xs text-text-muted py-2">
            <Loader2 size={ICON_PX.sm} className="animate-spin text-aqua" />
            Thinking...
          </div>
        )}

        {error && (
          <p className="text-xs text-red-500 py-1">{error}</p>
        )}

        <div ref={bottomRef} />

        {/* Scroll-to-bottom button */}
        {showScrollBtn && (
          <button
            data-testid="scroll-to-bottom"
            onClick={scrollToBottom}
            className="sticky bottom-2 ml-auto flex items-center justify-center w-7 h-7 rounded-full
                       bg-aqua text-white shadow-md hover:bg-teal-dark transition-colors"
            title="Scroll to bottom"
          >
            <ArrowDown size={ICON_PX.xs} />
          </button>
        )}
      </div>

      {/* Suggested chips (only before first user message) */}
      {userMessageCount === 0 && !isStreaming && (
        <div className="flex flex-wrap gap-1.5 mb-2 shrink-0">
          {SUGGESTED_QUESTIONS.map((q) => (
            <button
              key={q}
              onClick={() => handleSend(q)}
              disabled={creating}
              className="text-[11px] px-2.5 py-1 rounded-full border border-aqua/30 text-aqua hover:bg-aqua/10 transition-colors disabled:opacity-50"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 shrink-0">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this alert..."
          disabled={isStreaming || creating}
          className="flex-1 min-w-0 text-xs bg-surface border border-nn-border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-aqua placeholder:text-text-muted disabled:opacity-50"
        />
        <button
          onClick={() => handleSend(input)}
          disabled={!input.trim() || isStreaming || creating}
          className="shrink-0 p-2 rounded-lg bg-aqua text-white hover:bg-aqua/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {creating ? <Loader2 size={ICON_PX.sm} className="animate-spin" /> : <Send size={ICON_PX.sm} />}
        </button>
      </div>
    </div>
  );
}
